import { timeOfDaySchema } from "@medoma/models/activities";
import { ANY_TIME_OF_DAY, type INewActivity } from "@/api/Activities";
import { type IBatchTemplateActivity } from "@/api/Templates";
import {
  activityFrequencySchema,
  recurringOptionSchema,
} from "@/forms/AddActivity/Scheduling/schedulingModels";
import {
  getTimeFields,
  getTimespan,
} from "@/forms/AddActivity/activityTimeUtils";
import { convertDurationToMinutes } from "@/forms/AddActivity/activityDurationUtils";
import { buildNewActivity } from "@/forms/AddActivity/buildNewActivity";
import {
  calendarDateSchema,
  dateInputSchema,
} from "@medoma/models/date-and-time";

/**
 * Creates a new activity payload from a batch template activity.
 */
export const newActivityFromBatchTemplateActivity = ({
  activity,
  startDate,
  endDate,
  patientId,
  templateId,
  templateRevision,
}: {
  activity: IBatchTemplateActivity;
  startDate: string;
  endDate: string | undefined;
  patientId: string;
  templateId: string;
  templateRevision: number;
}): INewActivity => {
  const {
    category,
    title,
    description,
    doubleStaffing,
    frequency,
    recurrence,
    customRecurrenceInterval,
    customRecurrenceIntervalUnit,
    requiredCompetences,
    timeCategory,
    weekdays,
    hidden,
    measurements,
    timeslots,
    timeSensitivity,
    durationOption,
    customDuration,
    customDurationUnit,
  } = activity;

  const duration = convertDurationToMinutes(
    durationOption ?? "15",
    customDuration ?? null,
    customDurationUnit ?? null,
  );

  const isAnyTimeOfDay = timeCategory === timeOfDaySchema.enum.Any;
  const isRecurring =
    frequency === activityFrequencySchema.enum.recurring &&
    recurrence !== null &&
    recurrence !== undefined;
  const isRecurringAsInterval =
    recurrence !== recurringOptionSchema.enum.onSelectedWeekdays;

  const recurringTimeOfDayFields = isAnyTimeOfDay
    ? { isAnyTimeOfDay: true as const, timeslots: undefined }
    : {
        isAnyTimeOfDay: false as const,
        timeslots: (timeslots ?? []) as { time: string }[],
      };

  const timeFields = isRecurring
    ? {
        ...getTimeFields({
          isRecurringAsInterval,
          weekdays: weekdays ?? undefined,
          recurrence,
          customRecurrenceInterval: customRecurrenceInterval ?? null,
          customRecurrenceIntervalUnit: customRecurrenceIntervalUnit ?? null,
          ...recurringTimeOfDayFields,
        }),
        endDate: dateInputSchema.decode(endDate ?? "") ?? undefined,
      }
    : {
        time: isAnyTimeOfDay
          ? ANY_TIME_OF_DAY
          : (timeslots?.[0]?.time ?? ANY_TIME_OF_DAY),
      };

  return {
    ...buildNewActivity({
      category,
      title,
      description,
      duration,
      hidden: hidden ?? false,
      startDate: calendarDateSchema.decode(startDate),
      timespan: getTimespan({
        isAnyTimeOfDay,
        timeSensitivity: timeSensitivity ?? undefined,
      }),
      patientId,
      requiredCompetences,
      doubleStaffing,
      measurements,
      timeFields,
    }),
    templateId,
    templateRevision,
  };
};
